// Переиспользуемый компактный bar-chart блок «СВОДКА».
//
// HEAL-37 — четыре столбца вместо трёх, по одному на каждый статус:
//   «В норме» 🟢 · «Требуют внимания» 🟠 · «Вне нормы» 🔴 · «Без оценки» ⬜
//
// Что было не так. Столбец назывался «Требуют внимания», а считал ВНЕ НОРМЫ и
// красился красным — так было решено в HEAL-22, когда оранжевый тир нигде не
// назывался словом. После HEAL-35 оранжевый на карточке подписан «Требует
// внимания», и одна фраза стала значить на соседних экранах разное: тут 🔴,
// там 🟠. Тестировщик справедливо завёл это багом.
//
// Бакеты не пересекаются: сумма четырёх равна total.
//
// Используется на HomePending (Review) и HomeConfirmed (после сохранения).

import type { ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';

// Цвета — те же токены, что у тиров на карточках (tailwind.config.ts). Один
// статус обязан выглядеть одинаково в сводке и в списке за ней.
type Tone = 'ok' | 'attention' | 'bad' | 'neutral';

const BAR_BY_TONE: Record<Tone, string> = {
  ok: 'bg-primary',
  attention: 'bg-attention',
  bad: 'bg-bad',
  neutral: 'bg-gray-400',
};

const TEXT_BY_TONE: Record<Tone, string> = {
  ok: 'text-primary-text',
  attention: 'text-attention',
  bad: 'text-bad',
  neutral: 'text-muted-foreground',
};

type Props = {
  title?: string;
  total: number;
  /** 🟢 в норме и в оптимуме */
  normal: number;
  /** 🟠 в норме, но не оптимально */
  attention: number;
  /** 🔴 вне нормы */
  outOfRange: number;
  unknown?: number;
  totalUnit?: string;
  /**
   * HEAL-30: на Главной колонка «Без оценки» — вход в список с объяснением,
   * почему эти показатели не оценены. На экране ревью перехода нет (там ещё
   * ничего не сохранено), поэтому проп опциональный.
   */
  onUnknownClick?: () => void;
  /** HEAL-36: 🟠-столбец ведёт в «Маркеры» с отбором «Требуют внимания». */
  onAttentionClick?: () => void;
};

export function SummaryBarChart({
  title = 'Сводка',
  total,
  normal,
  attention,
  outOfRange,
  unknown = 0,
  totalUnit,
  onUnknownClick,
  onAttentionClick,
}: Props) {
  const max = Math.max(normal, attention, outOfRange, unknown, 1);
  const h = (n: number) => Math.max(16, Math.round((n / max) * 48));

  return (
    <div className="rounded-surface border border-border bg-surface p-3">
      <div className="mb-2 flex items-baseline justify-between">
        <div className="text-hl-small font-semibold uppercase tracking-wider text-muted-foreground">
          {title}
        </div>
        <div className="text-xs">
          <span className="text-sm font-bold">{total}</span>{' '}
          {totalUnit && <span className="text-muted-foreground">{totalUnit}</span>}
        </div>
      </div>
      {/* HEAL-42: ряд выровнен по ВЕРХУ, а общая нижняя линия столбиков держится
          фиксированной зоной внутри колонки (см. Bar). Было `items-end`: колонка =
          стек «число + столбик + подпись», поэтому двухстрочная подпись «Требуют
          внимания» делала колонку выше и толкала её столбик вверх — столбики
          стояли на разной высоте, диаграмма «прыгала». */}
      <div className="flex items-start justify-around gap-2 pt-1">
        <Bar count={normal} label="В норме" tone="ok" heightPx={h(normal)} />
        {/* 🟠 показываем, только если он есть: на бланке без оптимум-маркеров
            пустой столбец добавляет вопрос, а не ответ. */}
        {attention > 0 && (
          <Bar
            count={attention}
            label="Требуют внимания"
            tone="attention"
            heightPx={h(attention)}
            onClick={onAttentionClick}
          />
        )}
        <Bar count={outOfRange} label="Вне нормы" tone="bad" heightPx={h(outOfRange)} />
        {unknown > 0 && (
          <Bar
            count={unknown}
            label="Без оценки"
            tone="neutral"
            heightPx={h(unknown)}
            onClick={onUnknownClick}
          />
        )}
      </div>
    </div>
  );
}

function Bar({
  count,
  label,
  tone,
  heightPx,
  onClick,
}: {
  count: number;
  label: ReactNode;
  tone: Tone;
  heightPx: number;
  onClick?: () => void;
}) {
  const colorBar = BAR_BY_TONE[tone];
  const colorText = TEXT_BY_TONE[tone];
  // HEAL-42: три зоны фиксированной высоты, одинаковые во всех колонках:
  //   число — одна строка (leading-none);
  //   столбик — зона ровно в максимальную высоту столбика (48px = h(max)),
  //     сам столбик прижат к её низу → у всех колонок общая нижняя линия;
  //   подпись — место под две строки, чтобы «Требуют внимания» не растила колонку.
  const body = (
    <>
      <div className={`mb-0.5 text-lg font-bold leading-none ${colorText}`}>{count}</div>
      <div className="flex h-12 w-full items-end">
        <div
          className={`w-full rounded-t ${colorBar} ${count === 0 ? 'opacity-30' : ''}`}
          style={{ height: `${heightPx}px` }}
          aria-hidden="true"
        />
      </div>
      <div className="mt-1 min-h-[25px] text-center text-hl-small leading-tight text-muted-foreground">
        {label}
      </div>
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="relative flex flex-1 flex-col items-center rounded-control px-1 py-0.5 transition hover:bg-muted"
      >
        {/* HEAL-75: столбик кликабелен с HEAL-30, но выглядел частью диаграммы —
            нажать на столбик гистограммы никто не ожидает. */}
        <ChevronRight
          className="absolute right-0 top-0 h-3 w-3 text-gray-400"
          aria-hidden="true"
        />
        {body}
      </button>
    );
  }

  return <div className="flex flex-1 flex-col items-center">{body}</div>;
}
